import { FC, MouseEvent, useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { Copy, Lock, MoreHorizontal, Trash2, Unlock } from 'react-feather';
import styled from 'styled-components';
import { useEditorData, useEditorHistory } from '../lib/FabricJs';
import { useHandleCopyPaste } from '../lib/FabricJs/hooks/useKeyboardEvents';
import { useHandleObjectLock } from '../lib/FabricJs/hooks/useHandleObjectLock';
import { editorTheme } from '../theme';

const BAR_HEIGHT = 36;
// Clears Fabric's rotation handle, drawn 40px above the selection.
const BAR_GAP = 64;
const BAR_HALF_WIDTH = 80;
const BAR_EDGE_MARGIN = 4;

const Bar = styled.div`
  position: fixed;
  z-index: 10;
  height: ${BAR_HEIGHT}px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 4px;
  background: ${editorTheme.surface};
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(30, 20, 60, 0.18);
  transform: translateX(-50%);
`;

const BarButton = styled.button<{ active?: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  color: ${({ active }) => (active ? '#c2410c' : editorTheme.text)};
  &:hover {
    background: ${editorTheme.surfaceMuted};
  }
`;

type Position = { left: number; top: number };

const getBarPosition = (canvas: fabric.Canvas): Position | null => {
  const activeObject = canvas.getActiveObject();
  const canvasElement = canvas.getElement();
  if (!activeObject || !canvasElement) return null;
  const canvasRect = canvasElement.getBoundingClientRect();
  const workspaceRect = (
    canvasElement.closest('#canvas-editor-container') ?? canvasElement
  ).getBoundingClientRect();
  const objectRect = activeObject.getBoundingRect();
  const objectTop = canvasRect.top + objectRect.top;
  const objectLeft = canvasRect.left + objectRect.left;
  // Scrolled out of view (when zoomed in).
  if (
    objectTop > workspaceRect.bottom ||
    objectTop + objectRect.height < workspaceRect.top ||
    objectLeft > workspaceRect.right ||
    objectLeft + objectRect.width < workspaceRect.left
  ) {
    return null;
  }
  const above = objectTop - BAR_HEIGHT - BAR_GAP;
  const below = objectTop + objectRect.height + BAR_GAP;
  let top = workspaceRect.top + BAR_EDGE_MARGIN;
  if (above >= workspaceRect.top + BAR_EDGE_MARGIN) {
    top = above;
  } else if (below + BAR_HEIGHT <= workspaceRect.bottom - BAR_EDGE_MARGIN) {
    top = below;
  }
  const center = canvasRect.left + objectRect.left + objectRect.width / 2;
  const left = Math.min(
    Math.max(center, workspaceRect.left + BAR_HALF_WIDTH),
    workspaceRect.right - BAR_HALF_WIDTH,
  );
  return { left, top };
};

interface SelectionActionsProps {
  onMore: (point: { x: number; y: number }) => void;
}

const SelectionActions: FC<SelectionActionsProps> = ({ onMore }) => {
  const { canvas } = useEditorData();
  const { handleDuplicate, handleDelete } = useHandleCopyPaste(canvas);
  const { handleObjectLock, isLocked } = useHandleObjectLock();
  const { history } = useEditorHistory();
  const [position, setPosition] = useState<Position | null>(null);

  useEffect(() => {
    if (!canvas) return;
    const update = () => setPosition(getBarPosition(canvas));
    const hide = () => setPosition(null);
    const events: Record<string, () => void> = {
      'selection:created': update,
      'selection:updated': update,
      'selection:cleared': hide,
      'object:modified': update,
      'object:moving': hide,
      'object:scaling': hide,
      'object:rotating': hide,
      'mouse:up': update,
    };
    Object.entries(events).forEach(([name, handler]) =>
      canvas.on(name, handler),
    );
    // The canvas is resized (and re-zoomed) when the editor layout changes,
    // and scrolls when zoomed in.
    const observer = new ResizeObserver(update);
    observer.observe(canvas.getElement());
    const workspace = canvas.getElement().closest('#canvas-editor-container');
    workspace?.addEventListener('scroll', update);
    return () => {
      workspace?.removeEventListener('scroll', update);
      Object.entries(events).forEach(([name, handler]) =>
        canvas.off(name, handler),
      );
      observer.disconnect();
    };
  }, [canvas]);

  // Undo/redo moves objects without Fabric events; the canvas is rebuilt
  // after this effect runs, so wait a frame before measuring.
  useEffect(() => {
    if (!canvas) return;
    const frame = requestAnimationFrame(() =>
      setPosition(getBarPosition(canvas)),
    );
    return () => cancelAnimationFrame(frame);
  }, [canvas, history.present]);

  if (!canvas || !position || !canvas.getActiveObject()) return null;

  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <Bar
      role="toolbar"
      aria-label="Selection actions"
      style={position}
      onMouseDown={stop}
      onContextMenu={(e) => {
        e.preventDefault();
        stop(e);
      }}
    >
      <Tooltip title="Duplicate">
        <BarButton aria-label="Duplicate" onClick={handleDuplicate}>
          <Copy size={15} />
        </BarButton>
      </Tooltip>
      <Tooltip title={isLocked ? 'Unlock' : 'Lock'}>
        <BarButton
          aria-label={isLocked ? 'Unlock' : 'Lock'}
          active={isLocked}
          onClick={() => handleObjectLock()}
        >
          {isLocked ? <Lock size={15} /> : <Unlock size={15} />}
        </BarButton>
      </Tooltip>
      <Tooltip title="Delete">
        <BarButton
          aria-label="Delete"
          onClick={() => {
            handleDelete();
            setPosition(null);
          }}
        >
          <Trash2 size={15} />
        </BarButton>
      </Tooltip>
      <Tooltip title="More">
        <BarButton
          aria-label="More actions"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            onMore({
              x: rect.left,
              y: rect.bottom + 4,
            });
          }}
        >
          <MoreHorizontal size={15} />
        </BarButton>
      </Tooltip>
    </Bar>
  );
};

export default SelectionActions;
